import React, { useEffect, useState } from 'react';
import messages from './Messages';
import {
  RolesForm,
  StyledInput,
  AddRoleButton,
  RolesFormTitle,
  RoleInputContainer,
  RoleInputRow,
  Card,
} from './styles';
import Input from 'components/Input/Input';
import Select from 'components/Select';
import Collapsible from 'components/Collapsible/Collapsible';
import { IOption } from 'components/Select/Select';
import { getTags } from 'components/FileManager/ItemsViewer/Survey/SurveyEditor/GeneralQuestion/GeneralQuestionEditor';
import SelectWithPopover from 'components/Select/SelectWithPopover';
import { orange } from 'utils/style/colors';
import { ICompanyRole, RoleType } from 'store/companies/companiesTypes';
import { ITags } from 'store/providers/providerTypes';
import i18n from '../../../../i18n';

interface IRoleInputProps {
  addOrUpdateRole: (role: ICompanyRole) => Promise<any>;
  loading: boolean;
  companyId: number;
  cancelEdit: () => void;
  role?: ICompanyRole;
  maxRoleOrder?: number;
  allProjectTags?: string[];
  providerIds: number[];
  allRoles: ICompanyRole[];
  roleType?: RoleType;
  tags: ITags;
  parent?: ICompanyRole;
  hideAdvancedConfig?: boolean;
}

const RoleInput: React.FC<IRoleInputProps> = ({
  addOrUpdateRole,
  loading,
  companyId,
  role,
  cancelEdit,
  maxRoleOrder,
  allProjectTags,
  allRoles,
  roleType,
  tags,
  parent,
  providerIds,
  hideAdvancedConfig,
}) => {
  const isProjectRole = role
    ? Boolean(role?.isProjectRole)
    : roleType === 'InProject';

  const [projectTags, setProjectTags] = useState(
    role?.projectTags ?? ([] as string[])
  );
  const [surroundingMeRoles, setSurroundingMeRoles] = useState(
    role?.surroundingMeRoles ??
      (parent ? ([parent.id] as number[]) : ([] as number[]))
  );
  const [roleName, setroleName] = useState(role?.name);
  const [roleAutoAddPrefix, setRoleAutoAddPrefix] = useState(
    role?.autoAddPrefix
  );
  const [allowManualAssignToMember, setAllowManualAssignToMember] = useState(
    role ? role.allowManualAssignToMember : true
  );
  const [isUnique, setIsUnique] = useState(role?.isUnique || false);
  const [applyIfAllSurroundingExists, setApplyIfAllSurroundingExists] =
    useState(role?.applyIfAllSurroundingExists || false);
  const [compoundCreationWithRoleId, setCompoundCreationWithRoleId] = useState(
    role?.compoundCreationWithRoleId
  );
  const [compoundCreationIsUnique, setCompoundCreationIsUnique] = useState(
    role?.compoundCreationIsUnique
  );
  const [relatedTagId, setRelatedTagId] = useState(role?.relatedTagId);
  const [roleTypeState, setRoleType] = useState(role?.roleType || roleType);
  useEffect(() => {
    setRoleType(role?.roleType || roleType);
    setroleName(role?.name);
    setCompoundCreationIsUnique(role?.compoundCreationIsUnique);
    setCompoundCreationWithRoleId(role?.compoundCreationWithRoleId);
    setIsUnique(role?.isUnique || false);
  }, [role, parent, roleType]);
  let warnings = '';
  const tagName = tags[String(relatedTagId)]?.text || '';
  if (relatedTagId && tagName !== roleName) {
    if (relatedTagId < 0)
      warnings = warnings + messages.tagNameWillBeCreated(roleName);
    else
      warnings =
        warnings + messages.tagNameWillBeChange(tagName, roleName || '');
  }
  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const currentRoleName = roleName;
    if (currentRoleName) {
      const name = currentRoleName;
      // @ts-expect-error it's ok
      const RoleItem: ICompanyRole = {
        id: (role && role.id)!,
        name,
        stub: false,
        order: role?.order ?? (maxRoleOrder ?? 0) + 1,
        companyId,
        description: null,
        autoAddPrefix: roleAutoAddPrefix,
        isProjectRole,
        projectTags,
        surroundingMeRoles,
        applyIfAllSurroundingExists,
        isUnique,
        allowManualAssignToMember: allowManualAssignToMember || true,
        compoundCreationIsUnique,
        compoundCreationWithRoleId,
        relatedTagId,
        roleType: role?.roleType || roleTypeState || 'Post',
      };

      addOrUpdateRole(RoleItem).then(() => {
        setroleName('');
        // setroleDescription('');
        setRoleAutoAddPrefix(false);
        setRelatedTagId(undefined);
      });
    }
  };

  const roleTypes: IOption[] = [
    {
      value: 'Post',
      label: i18n.t('Post', { ns: 'companyManagement' }) as string,
    },
    {
      value: 'District',
      label: i18n.t('District', { ns: 'companyManagement' }) as string,
    },
    {
      value: 'Compound',
      label: i18n.t('Compound', { ns: 'companyManagement' }) as string,
    },
    {
      value: 'InProject',
      label: i18n.t('InProject', { ns: 'companyManagement' }) as string,
    },
  ];
  const allRoleOptions =
    allRoles
      ?.filter((it) => !it.isProjectRole)
      ?.map((it) => ({
        value: it.id,
        label: it.name,
        type: it.roleType,
      })) || [];
  return (
    <RolesForm onSubmit={onSubmit}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <RolesFormTitle>
          {role && role.id
            ? isProjectRole
              ? (i18n.t('editProjectRole', {
                  ns: 'companyManagement',
                }) as string)
              : role.roleType === 'District'
                ? (i18n.t('editDistrict', {
                    ns: 'companyManagement',
                  }) as string)
                : (i18n.t('editRole', { ns: 'companyManagement' }) as string)
            : roleType === 'District'
              ? (i18n.t('addDistrict', { ns: 'companyManagement' }) as string)
              : (i18n.t('addRoleToCompany', {
                  ns: 'companyManagement',
                }) as string)}
        </RolesFormTitle>
        {role?.roleType === null && (
          <Select
            placeholder={
              i18n.t('roleType', {
                ns: 'companyManagement',
              }) as string
            }
            onChangeValues={(values) => {
              setRoleType(values[0].value as RoleType);
              setRoleAutoAddPrefix(false);
            }}
            isRequired={false}
            options={roleTypes}
            values={roleTypes.filter((it) => it.value === roleTypeState)}
          />
        )}
      </div>
      <RoleInputContainer>
        <RoleInputRow>
          <StyledInput
            id="roleName"
            type="text"
            value={roleName}
            required={true}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setroleName(event.target.value);
            }}
            placeholder={
              roleTypeState === 'District'
                ? (i18n.t('districtHint', {
                    ns: 'companyManagement',
                  }) as string)
                : (i18n.t('roleHint', {
                    ns: 'companyManagement',
                  }) as string)
            }
            autoComplete="off"
          />
          {/*<StyledInput
            id="roleDescription"
            type="text"
            value={roleDescription}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setroleDescription(event.target.value);
            }}
            placeholder={messages.roleDescription}
            autoComplete="off"
          />*/}
        </RoleInputRow>
        {!isProjectRole && (
          <>
            <RoleInputRow>
              {!allowManualAssignToMember && (
                <Input
                  id={`allowManualAssignToMember${
                    role ? role.id : `-new + ${parent?.id}`
                  }`}
                  type="checkbox"
                  checked={!(allowManualAssignToMember || false)}
                  onChange={(event) => {
                    setAllowManualAssignToMember(event.target.checked);
                  }}
                  label={
                    i18n.t('autoAssignOnly', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  autoComplete="off"
                />
              )}
              {(roleTypeState === 'Post' ||
                roleTypeState === 'Compound' ||
                isUnique) && (
                <Input
                  id={`isUnique${role ? role.id : `-new + ${parent?.id}`}`}
                  type="checkbox"
                  checked={isUnique || false}
                  onChange={(event) => {
                    setIsUnique(event.target.checked);
                  }}
                  label={
                    i18n.t('isUnique', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  autoComplete="off"
                />
              )}
              {roleAutoAddPrefix && (
                <Input
                  id={`roleAutoAddPrefix${
                    role ? role.id : `-new + ${parent?.id}`
                  }`}
                  type="checkbox"
                  checked={roleAutoAddPrefix || false}
                  onChange={(event) => {
                    setRoleAutoAddPrefix(event.target.checked);
                  }}
                  label={
                    i18n.t('autoAddPrefix', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  autoComplete="off"
                />
              )}
            </RoleInputRow>
            {roleTypeState === 'Post' && !hideAdvancedConfig && (
              <Collapsible
                header={
                  i18n.t('compoundCreation', {
                    ns: 'companyManagement',
                  }) as string
                }
                style={{ overflow: 'visible' }}
                defaultOpen={!!compoundCreationWithRoleId}
              >
                <RoleInputRow>
                  <Select
                    placeholder={
                      i18n.t('compoundWith', {
                        ns: 'companyManagement',
                      }) as string
                    }
                    values={allRoleOptions.filter(
                      (it) => compoundCreationWithRoleId === it.value
                    )}
                    options={allRoleOptions}
                    multiChoice={false}
                    onChangeValues={(selected) => {
                      setCompoundCreationWithRoleId(Number(selected[0]?.value));
                    }}
                  />
                  <Input
                    id={`compoundCreationIsUnique${
                      role ? role.id : `-new + ${parent?.id}`
                    }`}
                    type="checkbox"
                    checked={compoundCreationIsUnique || false}
                    onChange={(event) => {
                      setCompoundCreationIsUnique(event.target.checked);
                    }}
                    label={
                      i18n.t('compoundCreationIsUnique', {
                        ns: 'companyManagement',
                      }) as string
                    }
                    autoComplete="off"
                  />
                </RoleInputRow>
              </Collapsible>
            )}
          </>
        )}
        {isProjectRole ? (
          <Select
            label={
              i18n.t('projectTypes', {
                ns: 'companyManagement',
              }) as string
            }
            values={projectTags.map((it: string) => ({ value: it, label: it }))}
            options={
              allProjectTags?.map((it: string) => ({ value: it, label: it })) ||
              []
            }
            multiChoice={true}
            onChangeValues={(selected) => {
              setProjectTags(selected.map((it) => it.value.toString()));
            }}
          />
        ) : (
          <>
            {(roleTypeState === 'District' || roleTypeState === 'Compound') && (
              <div>
                <Select
                  label={
                    roleTypeState === 'District'
                      ? (i18n.t('surroundingMeDistrict', {
                          ns: 'companyManagement',
                        }) as string)
                      : (i18n.t('surroundingMeRoles', {
                          ns: 'companyManagement',
                        }) as string)
                  }
                  values={allRoleOptions.filter((it) =>
                    surroundingMeRoles.includes(it.value)
                  )}
                  options={allRoleOptions.filter(
                    (it) =>
                      roleTypeState !== 'District' || it.type === 'District'
                  )}
                  multiChoice={true}
                  onChangeValues={(selected) => {
                    setSurroundingMeRoles(
                      selected.map((it) => Number(it.value))
                    );
                  }}
                />
                {(surroundingMeRoles.length >= 2 ||
                  applyIfAllSurroundingExists) && (
                  <Input
                    id={`applyIfAllSurroundingExists${
                      role ? role.id : `-new + ${parent?.id}`
                    }`}
                    type="checkbox"
                    checked={applyIfAllSurroundingExists || false}
                    onChange={(event) => {
                      setApplyIfAllSurroundingExists(event.target.checked);
                    }}
                    label={
                      i18n.t('applyIfAllSurroundingExists', {
                        ns: 'companyManagement',
                      }) as string
                    }
                    autoComplete="off"
                  />
                )}
              </div>
            )}
            {(roleTypeState === 'District' || relatedTagId) && (
              <Card>
                {
                  <Input
                    id={`autoCreateTag${
                      role ? role.id : `-new + ${parent?.id}`
                    }`}
                    type="checkbox"
                    checked={(relatedTagId || 0) < 0}
                    disabled={
                      !(
                        providerIds.length === 1 ||
                        surroundingMeRoles.length === 1
                      ) || (relatedTagId || 0) > 0
                    }
                    onChange={(e) =>
                      setRelatedTagId(e.target.checked ? -1 : undefined)
                    }
                    label={
                      i18n.t('autoCreateTag', {
                        ns: 'companyManagement',
                      }) as string
                    }
                  />
                }
                {relatedTagId !== -1 && (
                  <SelectWithPopover
                    multiChoice={false}
                    // @ts-expect-error it's ok
                    options={getTags(tags || {}, false)}
                    values={
                      relatedTagId
                        ? [
                            {
                              label: tagName,
                              value: relatedTagId,
                            },
                          ]
                        : []
                    }
                    onChangeValues={(values) => {
                      const value = values.length
                        ? Number(values[0]?.value)
                        : undefined;
                      setRelatedTagId(value);
                    }}
                    inputStyle={{ minHeight: 'initial', marginTop: '3px' }}
                    label={
                      i18n.t('relatedTag', {
                        ns: 'companyManagement',
                      }) as string
                    }
                    placeholder={
                      i18n.t('relatedTag', {
                        ns: 'companyManagement',
                      }) as string
                    }
                    isRequired={roleTypeState === 'District'}
                  />
                )}
              </Card>
            )}
          </>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '10px',
          }}
        >
          <AddRoleButton
            color="green"
            type="submit"
            loading={loading}
            disabled={!roleName}
          >
            {role && role.id
              ? (i18n.t('save', {
                  ns: 'companyManagement',
                }) as string)
              : (i18n.t('addName', {
                  ns: 'companyManagement',
                  name: `${roleType === 'Post' ? i18n.t('SPost', { ns: 'companyManagement' }) : roleType === 'District' ? i18n.t('SDistrict', { ns: 'companyManagement' }) : roleType === 'InProject' ? i18n.t('InProject', { ns: 'companyManagement' }) : ''}`,
                }) as string)}
          </AddRoleButton>
          {warnings && <p style={{ color: orange }}>{warnings}</p>}
          {role && role.id && (
            <AddRoleButton
              color="red"
              onClick={cancelEdit}
              loading={loading}
              style={{ float: 'left' }}
            >
              {
                i18n.t('cancel', {
                  ns: 'companyManagement',
                }) as string
              }
            </AddRoleButton>
          )}
        </div>
      </RoleInputContainer>
    </RolesForm>
  );
};

export default RoleInput;
